'use client';

import { useState, useEffect, useRef } from 'react';
import { mergeChatMessages, type ChatMessage as Message } from '@/app/lib/chat';

const STORAGE_KEY = 'ptc_chat';

function saveSession(sessionId: string, lastId: number) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ sessionId, lastId })); } catch {}
}

export default function ContactChatForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [followUp, setFollowUp] = useState('');
  const lastIdRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll for replies using id-based pagination
  useEffect(() => {
    if (!sessionId) return;

    const poll = async () => {
      try {
        const res = await fetch(`/api/chat/${sessionId}/messages?after=${lastIdRef.current}`);
        const data = await res.json();
        if (data.messages?.length > 0) {
          // Merge, don't append: the poll echoes back the user's own messages,
          // which we already rendered optimistically without an id.
          setMessages(prev => mergeChatMessages(prev, data.messages));
          const newLastId = data.messages[data.messages.length - 1].id;
          lastIdRef.current = newLastId;
          saveSession(sessionId, newLastId);
        }
      } catch {
        // Silently ignore
      }
    };

    const interval = setInterval(poll, 4000);
    return () => clearInterval(interval);
  }, [sessionId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/api/chat/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message }),
      });
      const data = await res.json();
      if (data.sessionId) {
        lastIdRef.current = 0;
        saveSession(data.sessionId, 0);
        // Signal HelpChat pill to adopt this session
        window.dispatchEvent(new CustomEvent('ptc:chat-started', { detail: { sessionId: data.sessionId } }));
        setMessages([{ role: 'user', content: message }]);
        setSessionId(data.sessionId);
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Could not send message. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFollowUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!followUp.trim()) return;
    const text = followUp.trim();
    setFollowUp('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    try {
      await fetch(`/api/chat/${sessionId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text }),
      });
    } catch {
      // Optimistic update already shown
    }
  };

  if (sessionId) {
    const hasSupport = messages.some(m => m.role === 'support');
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 text-sm text-green-400 font-semibold">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          Message sent — we'll reply here shortly
        </div>

        <div className="flex flex-col gap-3 max-h-72 overflow-y-auto pr-1">
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-red-700 text-white rounded-br-sm'
                  : 'bg-[#162847] text-slate-200 border border-amber-500/20 rounded-bl-sm'
              }`}>
                {msg.content}
              </div>
            </div>
          ))}
          {!hasSupport && (
            <div className="flex justify-start">
              <div className="max-w-[80%] px-4 py-2.5 rounded-2xl text-sm text-slate-500 bg-[#0a1628] border border-slate-700/40 rounded-bl-sm italic">
                We'll reply here shortly...
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleFollowUp} className="flex gap-2 pt-2 border-t border-amber-500/10">
          <input
            type="text"
            value={followUp}
            onChange={e => setFollowUp(e.target.value)}
            placeholder="Send a follow-up..."
            className="flex-1 px-4 py-3 rounded-xl border border-amber-500/20 bg-[#0a1628] text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400/50 outline-none text-sm transition-all"
          />
          <button
            type="submit"
            disabled={!followUp.trim()}
            className="px-5 py-3 bg-red-700 hover:bg-red-800 disabled:bg-slate-700 disabled:text-slate-500 text-white font-bold rounded-xl text-sm transition-colors active:scale-95"
          >
            →
          </button>
        </form>

        <p className="text-slate-500 text-xs">
          You can also continue this conversation via the 💬 Help button on any page.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Your Name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Jane Smith"
            required
            className="px-4 py-3 rounded-xl border border-amber-500/20 bg-[#0a1628] text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400/50 outline-none text-sm transition-all"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Your Email</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="jane@example.com"
            required
            className="px-4 py-3 rounded-xl border border-amber-500/20 bg-[#0a1628] text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400/50 outline-none text-sm transition-all"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">How can we help?</label>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Describe your question or issue..."
          required
          rows={5}
          className="px-4 py-3 rounded-xl border border-amber-500/20 bg-[#0a1628] text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400/50 outline-none text-sm resize-y transition-all"
        />
      </div>

      {error && (
        <p className="text-red-400 text-sm font-medium">✗ {error}</p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="px-8 py-4 bg-red-700 hover:bg-red-800 disabled:bg-slate-700 disabled:text-slate-400 text-white font-bold rounded-xl text-lg transition-all shadow-lg hover:shadow-red-800/40 active:scale-95"
      >
        {submitting ? 'Sending...' : 'Send Message →'}
      </button>
    </form>
  );
}
